import { centerFill, contentShell, shellGutter } from "@/shell/content-frame";
import { usePageAction } from "@/shell/page-action";
import {
  Body1,
  Checkbox,
  Tab,
  TabList,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  makeStyles,
  mergeClasses,
  tokens,
} from "@fluentui/react-components";
import { Board16Regular, CheckmarkCircle16Regular, ChevronDown16Regular, ChevronRight16Regular, DataPie16Regular, MoreHorizontal16Regular, TableSimple16Regular } from "@fluentui/react-icons";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FuiButton } from "@/components/fui-control";
import { useOwnWork, usePlanSurface } from "@/features/access/use-plan-access";
import { usePortfolioStyles } from "@/features/resources/PortfolioViews";
import { SegmentedControl } from "@/features/resources/SegmentedControl";
import { TaskAnalyticsView } from "@/features/tasks/TaskAnalyticsView";
import { TaskBoardView } from "@/features/tasks/TaskBoardView";
import { TaskBulkBar } from "@/features/tasks/TaskBulkBar";
import { openScopeInSchedule } from "@/features/tasks/links";
import { loadSavedColumns, saveColumns, TASK_COLUMNS, type TaskColumnKey } from "@/features/tasks/task-columns";
import { TaskFilterBar } from "@/features/tasks/TaskFilterBar";
import { TaskCommandMenu, taskMenuTarget } from "@/features/tasks/TaskCommandMenu";
import { groupAxisIcon } from "@/features/tasks/task-glyphs";
import { BOARD_GROUP_OPTIONS, LIST_GROUP_OPTIONS, TASK_STATUS_SCOPE_OPTIONS, filterFromParams, groupForView, parseTaskGroup, parseTaskView, taskScopeFromParams, taskStatusScope, withStatusScope, writeStatusScopeParam, writeTaskViewParams, type TaskStatusScope, type TaskView } from "@/features/tasks/task-views";
import { applyFilter, emptyFilter, groupTasks, sortTasks, type GroupKey, type SortKey, type TaskFilter } from "@/features/tasks/task-query";
import { AssigneeAvatars, ChecklistMini, DueDateChip, EmptyState, LabelChips, ScopeChips, StartDateChip, TaskPriorityChip, TaskStatusChip, useTaskContext } from "@/features/tasks/task-ui";
import { knownAssignees, PRIORITY_LABEL } from "@/store/execution-task-ops";
import { useProjectStore } from "@/store/project-store";
import { useSettingsHub } from "@/shell/settings-hub";

const useStyles = makeStyles({
  page: {
    ...contentShell,
    display: "flex",
    flexDirection: "column",
    height: "100%",
    minHeight: 0,
    position: "relative",
    backgroundColor: tokens.colorNeutralBackground2,
  },
  scroll: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    overflow: "auto",
    padding: `0 ${shellGutter} ${tokens.spacingVerticalXXL}`,
  },
  table: {
    /* 본문 흐름은 15px이다. 표는 시스템 기본(13px)을 유지한다. */
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightRegular,
  },
  headerCell: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightRegular,
  },
  groupRow: {
    backgroundColor: tokens.colorNeutralBackground3,
    cursor: "pointer",
  },
  groupCell: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXS,
    fontWeight: tokens.fontWeightRegular,
  },
  row: {
    cursor: "pointer",
    ":hover": { backgroundColor: tokens.colorNeutralBackground1Hover },
  },
  selectedRow: {
    backgroundColor: tokens.colorBrandBackground2,
  },
  title: {
    minWidth: 0,
    overflow: "hidden",
  },
  titleText: {
    display: "block",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  done: {
    textDecoration: "line-through",
    color: tokens.colorNeutralForeground3,
  },
  cellNoWrap: {
    whiteSpace: "nowrap",
  },
  /** Date columns: right-aligned tabular numbers, like the records register. */
  numCell: {
    textAlign: "right",
    whiteSpace: "nowrap",
    fontVariantNumeric: "tabular-nums",
    justifyContent: "flex-end",
    "& .fui-TableHeaderCell__button, & .fui-TableCellLayout": { justifyContent: "flex-end" },
  },
  actionCell: {
    width: "48px",
    textAlign: "center",
  },
  empty: centerFill,
});

export function ProjectTasksPage() {
  const styles = useStyles();
  const surface = usePlanSurface();
  const canEditTask = surface.editTask;
  const canConfigureBoard = surface.editWbs;
  const own = useOwnWork();
  const pStyles = usePortfolioStyles();
  const ctx = useTaskContext();
  const projectId = useProjectStore((s) => s.activeId) ?? "";
  const tasks = useProjectStore((s) => s.project.executionTasks);
  const executionTasksAll = tasks;
  const selection = useProjectStore((s) => s.executionSelection);
  const s = useProjectStore.getState;
  const [params, setParams] = useSearchParams();

  const [visibleColumns, setVisibleColumns] = useState<TaskColumnKey[]>(loadSavedColumns);
  const handleColumnsChange = (cols: TaskColumnKey[]) => {
    setVisibleColumns(cols);
    saveColumns(cols);
  };

  const [filter, setFilter] = useState<TaskFilter>(() => withStatusScope(filterFromParams(params), taskScopeFromParams(params)));
  const [view, setViewState] = useState<TaskView>(() => parseTaskView(params));
  const viewRef = useRef(view);
  const [group, setGroupState] = useState<GroupKey>(() => parseTaskGroup(params, parseTaskView(params)));
  const [sort, setSort] = useState<SortKey>("due");
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [quick, setQuick] = useState("");
  const lastClickedRef = useRef<string | null>(null);

  // ?open=<id> deep link → drawer
  useEffect(() => {
    const open = params.get("open");
    if (open && tasks.some((t) => t.id === open)) {
      s().setExecutionFocus(open);
      const next = new URLSearchParams(params);
      next.delete("open");
      setParams(next, { replace: true });
    }
  }, [params, tasks, s, setParams]);

  useEffect(() => () => s().setExecutionSelection([]), [s]);

  const setTaskFilter = (next: TaskFilter) => setFilter(next);

  const setView = (next: TaskView) => {
    if (next === view) return;
    viewRef.current = next;
    if (next !== "list") s().setExecutionSelection([]);
    const nextGroup = groupForView(group, next);
    setViewState(next);
    setFilter((current) => (current.query ? { ...current, query: "" } : current));
    if (nextGroup !== group) setGroupState(nextGroup);
    setParams(writeTaskViewParams(params, next, nextGroup), { replace: true });
  };

  // 팔레트·딥링크처럼 같은 화면에서 주소의 view만 바뀌는 이동도 따라간다.
  const urlView = parseTaskView(params);
  useEffect(() => {
    if (viewRef.current === urlView) return;
    viewRef.current = urlView;
    setViewState(urlView);
    setFilter((current) => (current.query ? { ...current, query: "" } : current));
  }, [urlView]);

  /** 분석 보기에서 막대·지표를 누르면 그 조건이 걸린 목록으로 바꾼다. 같은 화면이라 필터 state를 직접 갈아 끼운다. */
  const drillToList = (query: Record<string, string>) => {
    const q = new URLSearchParams(query);
    const nextGroup = parseTaskGroup(q, "list");
    viewRef.current = "list";
    setTaskFilter(filterFromParams(q));
    setViewState("list");
    setGroupState(nextGroup);
    setParams(writeTaskViewParams(q, "list", nextGroup), { replace: true });
  };

  const statusScope = taskStatusScope(filter);
  const setStatusScope = (scope: TaskStatusScope) => {
    setTaskFilter(withStatusScope(filter, scope));
    const next = writeTaskViewParams(params, view, group);
    writeStatusScopeParam(next, scope);
    setParams(next, { replace: true });
  };

  const setGroup = (next: GroupKey) => {
    const nextGroup = groupForView(next, view);
    setGroupState(nextGroup);
    setParams(writeTaskViewParams(params, view, nextGroup), { replace: true });
  };

  const visible = useMemo(() => sortTasks(applyFilter(tasks, filter, ctx), view === "board" ? "manual" : sort), [tasks, filter, ctx, sort, view]);
  const groups = useMemo(() => groupTasks(visible, group, ctx), [visible, group, ctx]);
  const assignees = useMemo(() => knownAssignees({ tasks: ctx.project.tasks, executionTasks: executionTasksAll }), [ctx.project.tasks, executionTasksAll]);
  const allVisibleIds = visible.map((t) => t.id);
  const allSelected = allVisibleIds.length > 0 && allVisibleIds.every((id) => selection.includes(id));
  const inSelectMode = selection.length > 0;

  const colSpan = useMemo(() => {
    let count = canEditTask ? 3 : 2; // Checkbox + Title + 동작
    for (const c of visibleColumns) {
      if (c === "bucket" && group === "bucket") continue;
      count++;
    }
    return count;
  }, [visibleColumns, group, canEditTask]);

  const exitSelect = () => {
    s().setExecutionSelection([]);
  };

  useEffect(() => {
    if (!inSelectMode) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (e.defaultPrevented) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) return;
      if (document.querySelector('[role="dialog"]') || document.querySelector('[role="menu"]')) return;
      e.preventDefault();
      exitSelect();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [inSelectMode, s]);

  const handleHeaderNewTask = () => {
    s().addExecutionTask(
      {
        bucketId:
          filter.bucketIds.length === 1
            ? filter.bucketIds[0]
            : view === "board" && group === "bucket"
              ? ctx.project.planner.buckets[0]?.id
              : undefined,
        linkedTaskIds: filter.linkedTaskIds.length ? filter.linkedTaskIds : [],
        assignees: filter.assignees.length === 1 && filter.assignees[0] ? [filter.assignees[0]] : [],
        status: filter.statuses.length === 1 && filter.statuses[0] !== "unset" ? filter.statuses[0] : undefined,
      },
      { open: true },
    );
  };

  usePageAction(own.canCreateTask ? { label: "Task 추가", icon: CheckmarkCircle16Regular, onClick: handleHeaderNewTask, inHeader: true } : null);

  return (
    <div className={styles.page}>
      <TaskFilterBar
        title="작업"
        description="이 플랜의 담당, 마감, 진행"
        headingAction={
          own.canCreateTask ? (
            <FuiButton appearance="primary" icon={<CheckmarkCircle16Regular />} onClick={handleHeaderNewTask}>
              Task 추가
            </FuiButton>
          ) : undefined
        }
        filter={filter}
        onChange={setTaskFilter}
        ctx={ctx}
        showFilters={view !== "analytics"}
        statusInScope
        sections={
          <TabList
            size="small"
            selectedValue={view}
            aria-label="작업"
            onTabSelect={(_, data) => {
              const next = data.value;
              if (next === "list" || next === "board" || next === "analytics") setView(next);
            }}
          >
            <Tab value="list" icon={<TableSimple16Regular />}>목록</Tab>
            <Tab value="board" icon={<Board16Regular />}>보드</Tab>
            <Tab value="analytics" icon={<DataPie16Regular />}>분석</Tab>
          </TabList>
        }
        scope={
          view === "analytics" ? undefined : (
            <SegmentedControl
              ariaLabel="상태"
              value={statusScope}
              onChange={setStatusScope}
              options={TASK_STATUS_SCOPE_OPTIONS}
            />
          )
        }
        leading={
          inSelectMode && view === "list" && canEditTask && !own.restricted ? (
            <TaskBulkBar inline ids={selection} onClear={() => s().setExecutionSelection([])} />
          ) : undefined
        }
        group={
          view === "analytics"
            ? undefined
            : {
                value: group,
                onChange: setGroup,
                options: view === "board" ? BOARD_GROUP_OPTIONS : LIST_GROUP_OPTIONS,
                labelPrefix: view === "board" ? "열" : "그룹",
              }
        }
        sort={view === "list" ? { value: sort, onChange: setSort } : undefined}
        columns={
          view === "list"
            ? {
                visible: visibleColumns,
                onChange: handleColumnsChange,
                options: TASK_COLUMNS,
              }
            : undefined
        }
        assignees={assignees}
        displayAction={canConfigureBoard ? { label: "버킷·라벨", onClick: () => useSettingsHub.getState().open("plan", "buckets") } : undefined}
      />

      {view === "analytics" ? (
        <TaskAnalyticsView onDrill={drillToList} />
      ) : view === "board" ? (
        <TaskBoardView
          tasks={tasks}
          visibleCount={visible.length}
          groups={groups}
          axis={group}
          onClearFilter={() => setTaskFilter(emptyFilter())}
        />
      ) : (
        <div className={styles.scroll}>
        {tasks.length === 0 ? (
          <div className={styles.empty}>
            <EmptyState
              title="아직 Task가 없습니다"
              hint={own.canCreateTask ? "WBS는 범위, Task는 오늘 할 일입니다. 아래 버튼을 눌러 첫 Task를 만드세요. 상태·담당·날짜는 나중에 정해도 됩니다." : "이 플랜에 등록된 Task가 없습니다."}
              action={
                own.canCreateTask ? (
                  <FuiButton appearance="primary" icon={<CheckmarkCircle16Regular />} onClick={() => s().addExecutionTask({})}>
                    Task 추가
                  </FuiButton>
                ) : undefined
              }
            />
          </div>
        ) : visible.length === 0 ? (
          <div className={styles.empty}>
            <EmptyState
              title="필터와 일치하는 Task가 없습니다"
              hint="필터를 지우거나 다른 조건을 선택하세요."
              action={<FuiButton onClick={() => setTaskFilter(emptyFilter())}>필터 지우기</FuiButton>}
            />
          </div>
        ) : (
          <div className={mergeClasses(pStyles.tableWrap, styles.table)}>
          <Table size="small" className={pStyles.fluent} aria-label="Task 목록">
            <TableHeader>
              <TableRow className={pStyles.headRow}>
                <TableHeaderCell className={mergeClasses(pStyles.head, styles.headerCell)} style={{ width: 32 }}>
                  <Checkbox
                    checked={allSelected}
                    aria-label="모두 선택"
                    onChange={(_, d) => s().setExecutionSelection(d.checked ? allVisibleIds : [])}
                  />
                </TableHeaderCell>
                <TableHeaderCell className={mergeClasses(pStyles.head, styles.headerCell)}>제목</TableHeaderCell>
                {visibleColumns.includes("status") && <TableHeaderCell className={mergeClasses(pStyles.head, styles.headerCell)} style={{ width: 88 }}>상태</TableHeaderCell>}
                {visibleColumns.includes("priority") && <TableHeaderCell className={mergeClasses(pStyles.head, styles.headerCell)} style={{ width: 72 }}>우선순위</TableHeaderCell>}
                {visibleColumns.includes("assignee") && <TableHeaderCell className={mergeClasses(pStyles.head, styles.headerCell)} style={{ width: 76 }}>담당</TableHeaderCell>}
                {visibleColumns.includes("start") && <TableHeaderCell className={mergeClasses(pStyles.head, styles.headerCell, styles.numCell)} style={{ width: 96 }}>시작일</TableHeaderCell>}
                {visibleColumns.includes("due") && <TableHeaderCell className={mergeClasses(pStyles.head, styles.headerCell, styles.numCell)} style={{ width: 96 }}>마감일</TableHeaderCell>}
                {visibleColumns.includes("bucket") && group !== "bucket" && <TableHeaderCell className={mergeClasses(pStyles.head, styles.headerCell)} style={{ width: 96 }}>버킷</TableHeaderCell>}
                {visibleColumns.includes("scope") && <TableHeaderCell className={mergeClasses(pStyles.head, styles.headerCell)} style={{ width: 200 }}>WBS · 작업</TableHeaderCell>}
                {visibleColumns.includes("labels") && <TableHeaderCell className={mergeClasses(pStyles.head, styles.headerCell)} style={{ width: 110 }}>라벨</TableHeaderCell>}
                {visibleColumns.includes("checklist") && <TableHeaderCell className={mergeClasses(pStyles.head, styles.headerCell)} style={{ width: 76 }}>체크</TableHeaderCell>}
                {canEditTask && <TableHeaderCell className={mergeClasses(pStyles.head, styles.headerCell, styles.actionCell)} aria-label="동작" />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {groups.map((g) => {
                const isCollapsed = collapsed.has(g.key);
                const showHeader = group !== "none";
                return (
                  <GroupRows
                    key={g.key}
                    showHeader={showHeader}
                    label={g.label}
                    icon={groupAxisIcon(group)}
                    count={g.tasks.length}
                    collapsed={isCollapsed}
                    onToggle={() => setCollapsed((c) => toggleSet(c, g.key))}
                    colSpan={colSpan}
                  >
                    {!isCollapsed &&
                      g.tasks.map((t) => (
                        <TableRow
                          key={`${g.key}-${t.id}`}
                          className={mergeClasses(styles.row, selection.includes(t.id) && styles.selectedRow)}
                          onClick={(e) => {
                            if (e.shiftKey) {
                              const ids = allVisibleIds;
                              const prev = lastClickedRef.current;
                              const b = ids.indexOf(t.id);
                              const a = prev ? ids.indexOf(prev) : -1;
                              if (a >= 0 && b >= 0) {
                                const [from, to] = a < b ? [a, b] : [b, a];
                                s().setExecutionSelection(ids.slice(from, to + 1));
                              } else {
                                s().setExecutionSelection([t.id]);
                                lastClickedRef.current = t.id;
                              }
                              return;
                            }
                            if (e.metaKey || e.ctrlKey) {
                              s().toggleExecutionSelection(t.id);
                              lastClickedRef.current = t.id;
                              return;
                            }
                            if (inSelectMode) {
                              s().toggleExecutionSelection(t.id);
                              lastClickedRef.current = t.id;
                              return;
                            }
                            lastClickedRef.current = t.id;
                            s().setExecutionFocus(t.id);
                          }}
                          data-execution-id={t.id}
                        >
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={selection.includes(t.id)}
                              aria-label="선택"
                              onChange={() => {
                                s().toggleExecutionSelection(t.id);
                                lastClickedRef.current = t.id;
                              }}
                            />
                          </TableCell>
                          <TableCell className={mergeClasses(styles.cellNoWrap, styles.title)}>
                            <Body1 className={mergeClasses(styles.titleText, t.status === "done" && styles.done)}>
                              {t.title || <span style={{ color: tokens.colorNeutralForeground4 }}>제목 없음</span>}
                            </Body1>
                          </TableCell>
                          {visibleColumns.includes("status") && (
                            <TableCell className={styles.cellNoWrap}>
                              <TaskStatusChip status={t.status} />
                            </TableCell>
                          )}
                          {visibleColumns.includes("priority") && (
                            <TableCell className={styles.cellNoWrap}>
                              <TaskPriorityChip priority={t.priority} />
                            </TableCell>
                          )}
                          {visibleColumns.includes("assignee") && (
                            <TableCell>
                              <AssigneeAvatars names={t.assignees} size={20} />
                            </TableCell>
                          )}
                          {visibleColumns.includes("start") && (
                            <TableCell className={styles.numCell}>
                              <StartDateChip task={t} />
                            </TableCell>
                          )}
                          {visibleColumns.includes("due") && (
                            <TableCell className={styles.numCell}>
                              <DueDateChip task={t} today={ctx.today} />
                            </TableCell>
                          )}
                          {visibleColumns.includes("bucket") && group !== "bucket" && (
                            <TableCell className={styles.cellNoWrap}>
                              {ctx.project.planner.buckets.find((b) => b.id === t.bucketId)?.name ?? "—"}
                            </TableCell>
                          )}
                          {visibleColumns.includes("scope") && (
                            <TableCell className={styles.cellNoWrap}>
                              <ScopeChips task={t} ctx={ctx} max={1} nowrap onOpen={(id) => openScopeInSchedule(projectId, id)} />
                            </TableCell>
                          )}
                          {visibleColumns.includes("labels") && (
                            <TableCell className={styles.cellNoWrap}>
                              <LabelChips labelIds={t.labelIds} labels={ctx.project.planner.labels} max={1} nowrap />
                            </TableCell>
                          )}
                          {visibleColumns.includes("checklist") && (
                            <TableCell className={styles.cellNoWrap}>
                              <ChecklistMini task={t} />
                            </TableCell>
                          )}
                          {canEditTask && (
                            <TableCell className={styles.actionCell} onClick={(e) => e.stopPropagation()}>
                              <TaskCommandMenu
                                task={taskMenuTarget(t)}
                                canWrite={own.executionOwn(t.assignees)}
                                assignees={assignees}
                                onOpen={() => s().setExecutionFocus(t.id)}
                                onStatus={(status) => s().setExecutionStatus(t.id, status)}
                                onPriority={(priority) => s().updateExecutionTasks([t.id], { priority }, `우선순위 → ${priority ? PRIORITY_LABEL[priority] : "미정"}`)}
                                onAssignees={(names) => s().updateExecutionTasks([t.id], { assignees: names }, "담당 변경")}
                                onDuplicate={() => s().duplicateExecutionTask(t.id)}
                                onDelete={() => s().deleteExecutionTasks([t.id])}
                                trigger={<FuiButton appearance="subtle" icon={<MoreHorizontal16Regular />} aria-label={`${t.title || "Task"} 동작`} />}
                              />
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                  </GroupRows>
                );
              })}
            </TableBody>
          </Table>
          </div>
        )}
        </div>
      )}
    </div>
  );
}

function GroupRows({ showHeader, label, icon, count, collapsed, onToggle, colSpan, children }: { showHeader: boolean; label: string; icon?: React.ReactNode; count: number; collapsed: boolean; onToggle: () => void; colSpan: number; children: React.ReactNode }) {
  const styles = useStyles();
  if (!showHeader) return <>{children}</>;
  if (count === 0 && !showHeader) return null;

  const renderedLabel = label.includes(" › ") ? (() => {
    const parts = label.split(" › ");
    const ancestors = parts.slice(0, -1).join(" › ");
    const leaf = parts[parts.length - 1];
    return (
      <span className="inline-flex items-center gap-1 min-w-0 truncate">
        <span style={{ color: tokens.colorNeutralForeground3 }}>{ancestors} ›</span>
        <span>{leaf}</span>
      </span>
    );
  })() : label;

  return (
    <>
      <TableRow className={styles.groupRow} onClick={onToggle} aria-expanded={!collapsed}>
        <TableCell colSpan={colSpan}>
          <span className={styles.groupCell}>
            {collapsed ? <ChevronRight16Regular /> : <ChevronDown16Regular />}
            {icon}
            {renderedLabel}
            <span style={{ color: tokens.colorNeutralForeground3 }}>{count}</span>
          </span>
        </TableCell>
      </TableRow>
      {children}
    </>
  );
}

function toggleSet(set: Set<string>, key: string): Set<string> {
  const next = new Set(set);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  return next;
}
